/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * EventEditModal — premium create/edit modal for the services-app calendar.
 * Field parity with the admin ProfessionalEventModal, built on the ManagementUI
 * primitives + DateTimeModal. Controlled-but-self-managing: seeds from `initial`
 * on open and emits assembled values via onSave; the parent runs the conflict
 * guard and persistence.
 */

import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import Icon from './management/SafeIcon';
import { useManagementTheme } from '../helpers/managementTheme';
import { EditableField, SwitchField, ChipSelectField, PrimaryButton } from './management/ManagementUI';
import { ModalKeyboardAvoider, ModalScrollBody } from './KeyboardAware';
import DateTimeModal from './DateTimeModal';
import calendarApi, { combineDateTime } from '../helpers/calendarApi';

const todayStr = () => {
    const d = new Date();

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Preset lead times for one reminder row (minutes before start). Values absent
 * from this list render as the "custom" chip with a free minutes input.
 */
const REMINDER_PRESETS = [
    { value: 0, key: 'reminder_at_time', defaultValue: 'Kezdéskor' },
    { value: 5, key: 'reminder_5_min', defaultValue: '5 perccel előtte' },
    { value: 10, key: 'calendar_reminder_10_min', defaultValue: '10 perccel előtte' },
    { value: 15, key: 'reminder_15_min', defaultValue: '15 perccel előtte' },
    { value: 30, key: 'reminder_30_min', defaultValue: '30 perccel előtte' },
    { value: 60, key: 'reminder_1_hour', defaultValue: '1 órával előtte' },
    { value: 120, key: 'calendar_reminder_2_hours', defaultValue: '2 órával előtte' },
    { value: 1440, key: 'reminder_1_day', defaultValue: '1 nappal előtte' }
];

const REMINDER_PRESET_VALUES = REMINDER_PRESETS.map((preset) => preset.value);

/**
 * Reminder alert styles: 'notification' = quiet backend push (the server cron
 * delivers it), 'alarm' = loud local alarm the phone schedules offline too.
 */
const REMINDER_STYLES = [
    { value: 'notification', icon: 'bell-outline', key: 'calendar_reminder_style_notification', defaultValue: 'Értesítés' },
    { value: 'alarm', icon: 'alarm', key: 'calendar_reminder_style_alarm', defaultValue: 'Ébresztő' }
];

/** Most reminder rows one event can carry (mirrors the admin twin's cap). */
const MAX_REMINDERS = 5;

/**
 * Normalize one reminders array coming from the API / the editor: integer
 * non-negative minutes, valid style, deduped by minutesBefore (first wins),
 * capped at {@link MAX_REMINDERS} and sorted ascending.
 *
 * @param {Array<{minutesBefore: number, style: string}>} reminders - Raw rows.
 * @returns {Array<{minutesBefore: number, style: string}>} sanitized rows.
 */
const sanitizeReminders = (reminders) => {
    const seen = new Set();
    const clean = [];

    (Array.isArray(reminders) ? reminders : []).forEach((reminder) => {
        if (!reminder || typeof reminder !== 'object') {
            return;
        }
        const minutesBefore = Math.max(0, Math.floor(Number(reminder.minutesBefore)) || 0);

        if (seen.has(minutesBefore) || clean.length >= MAX_REMINDERS) {
            return;
        }
        seen.add(minutesBefore);
        clean.push({
            minutesBefore,
            style: reminder.style === 'alarm' ? 'alarm' : 'notification'
        });
    });

    return clean.sort((a, b) => a.minutesBefore - b.minutesBefore);
};

/** MaterialCommunityIcons glyph per meeting platform. */
const MEETING_PROVIDER_ICON = {
    google_meet: 'google',
    teams: 'microsoft-teams',
    zoom: 'video-box',
    webex: 'video-account',
    whereby: 'web',
    jitsi: 'video'
};

/**
 * Offered until the capability call answers, so the buttons are never empty on a
 * slow network. Mirrors the backend catalogue's generatable entries.
 */
const FALLBACK_MEETING_PROVIDERS = [
    { value: 'google_meet', label: 'Google Meet' },
    { value: 'teams', label: 'Microsoft Teams' },
    { value: 'jitsi', label: 'Jitsi Meet' }
];

const EventEditModal = ({
    visible,
    mode = 'create',
    busyMode = false,
    initial,
    experts = [],
    showExpert = false,
    connection = {},
    saving = false,
    onClose,
    onSave,
    onDelete
}) => {
    const { t } = useTranslation();
    const m = useManagementTheme();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState(todayStr());
    const [startTime, setStartTime] = useState('09:00');
    const [endDate, setEndDate] = useState(todayStr());
    const [endTime, setEndTime] = useState('10:00');
    const [expertId, setExpertId] = useState('');
    const [clientName, setClientName] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    // Multi-reminder rows ({ minutesBefore, style, custom? }): `custom` marks a
    // row edited through the free minutes input rather than a preset chip.
    const [reminders, setReminders] = useState([{ minutesBefore: 30, style: 'notification' }]);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [notifyBySms, setNotifyBySms] = useState(false);
    const [notifyExpert, setNotifyExpert] = useState(true);
    const [syncWithGoogle, setSyncWithGoogle] = useState(false);
    const [syncWithApple, setSyncWithApple] = useState(false);
    const [meetingUrl, setMeetingUrl] = useState('');
    const [meetingProvider, setMeetingProvider] = useState('');
    // Which provider is currently being minted ('' = idle), so only that button spins.
    const [generatingMeeting, setGeneratingMeeting] = useState('');
    // Platforms this deployment can actually mint a link on, from the backend.
    const [meetingProviders, setMeetingProviders] = useState(FALLBACK_MEETING_PROVIDERS);
    const [picker, setPicker] = useState(null); // 'start' | 'end' | null

    // Refresh the offered platforms every time the modal opens, so an account
    // linked since last time shows up without restarting the app.
    useEffect(() => {
        if (!visible) {
            return undefined;
        }
        let mounted = true;

        calendarApi.fetchMeetingProviders()
            .then((providers) => {
                const usable = providers.filter((entry) => entry.generatable && entry.configured);

                if (mounted && usable.length) {
                    setMeetingProviders(usable);
                }
            })
            .catch(() => { /* Keep the fallback list. */ });

        return () => { mounted = false; };
    }, [visible]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        const init = initial || {};

        setTitle(init.title || '');
        setDescription(init.description || '');
        setStartDate(init.startDate || todayStr());
        setStartTime(init.startTime || '09:00');
        setEndDate(init.endDate || todayStr());
        setEndTime(init.endTime || '10:00');
        setExpertId(init.expertId || '');
        setClientName(init.clientName || '');
        setClientPhone(init.clientPhone || '');
        setClientEmail(init.clientEmail || '');
        // Seed the reminder rows from the event's reminders array; legacy events
        // (docs saved before multi-reminders) fall back to their single
        // reminderTime as one quiet 'notification' row.
        const seeded = sanitizeReminders(Array.isArray(init.reminders) && init.reminders.length
            ? init.reminders
            : [{ minutesBefore: init.reminderMinutes ?? 30, style: 'notification' }]);

        setReminders(seeded.length ? seeded : [{ minutesBefore: 30, style: 'notification' }]);
        setNotificationsEnabled(init.notificationsEnabled !== false);
        setNotifyByEmail(init.notifyByEmail !== false);
        setNotifyBySms(Boolean(init.notifyBySms));
        setNotifyExpert(init.notifyExpert !== false);
        setSyncWithGoogle(Boolean(init.syncWithGoogle));
        setSyncWithApple(Boolean(init.syncWithApple));
        setMeetingUrl(init.meetingUrl || '');
        setMeetingProvider(init.meetingProvider || '');
        setGeneratingMeeting('');
    }, [visible, initial]);

    /**
     * Ask the backend for a meeting link hosted by the signed-in user's OWN
     * Google / Microsoft account.
     * @param {string} provider - 'google_meet' | 'teams'
     * @returns {Promise<?string>} the join URL, or null when the API returns none
     */
    const requestMeetingLink = (provider) => calendarApi.generateMeetingLink(provider, {
        title,
        description,
        start: combineDateTime(startDate, startTime),
        end: combineDateTime(endDate, endTime)
    });

    /**
     * Mint a real Google Meet / Teams link from the user's connected calendar
     * account and drop it into the meeting-link field. When no account is linked
     * yet (or its stored consent died), the backend answers `needsConnect` with a
     * ready-to-open consent URL: the provider's OAuth page is opened, the app
     * waits for the connection to appear, and the generation is retried once.
     * Still-failing cases no-op (the field stays editable for a pasted link).
     * @param {string} provider - 'google_meet' | 'teams'
     * @returns {Promise<void>}
     */
    const handleGenerateMeeting = async (provider) => {
        if (generatingMeeting) {
            return;
        }
        setGeneratingMeeting(provider);
        try {
            let link;

            try {
                link = await requestMeetingLink(provider);
            } catch (error) {
                if (!error?.needsConnect) {
                    throw error;
                }
                const connected = await calendarApi.connectProviderAccount(provider, error.authUrl);

                if (!connected) {
                    throw error;
                }
                link = await requestMeetingLink(provider);
            }

            if (link) {
                setMeetingUrl(link);
                setMeetingProvider(provider);
            }
        } catch (_error) {
            /* Not connected / API failure — leave the field for manual entry. */
        } finally {
            setGeneratingMeeting('');
        }
    };

    const expertOptions = experts.map((e) => ({
        value: String(e._id),
        label: e.displayName || `${e.name?.first || ''} ${e.name?.last || ''}`.trim() || 'Szakértő'
    }));

    /**
     * Patch one reminder row in place (index-stable so rows never jump while
     * being edited; ordering/dedup happens in {@link sanitizeReminders} on save).
     * @param {number} index - Row index.
     * @param {Object} patch - Partial row ({ minutesBefore?, style?, custom? }).
     * @returns {void}
     */
    const updateReminder = (index, patch) => {
        setReminders((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const removeReminder = (index) => {
        setReminders((prev) => prev.filter((_, i) => i !== index));
    };

    const addReminder = () => {
        setReminders((prev) => {
            if (prev.length >= MAX_REMINDERS) {
                return prev;
            }
            const used = prev.map((row) => row.minutesBefore);
            const free = REMINDER_PRESET_VALUES.find((value) => !used.includes(value));

            return [...prev, { minutesBefore: free ?? 30, style: 'notification' }];
        });
    };

    /**
     * One reminder row: preset lead-time chips (+ a "custom" chip revealing a
     * free minutes input) and the notification-vs-alarm style toggle, in the
     * sheet's own chip/pill vocabulary.
     * @param {{minutesBefore: number, style: string, custom?: boolean}} row - Row value.
     * @param {number} index - Row index.
     * @returns {JSX.Element}
     */
    const renderReminderRow = (row, index) => {
        const isCustom = Boolean(row.custom) || !REMINDER_PRESET_VALUES.includes(row.minutesBefore);

        return (
            <View key={index} style={[styles.reminderRow, { borderColor: m.colors.border, backgroundColor: m.colors.inputBg }]}>
                <View style={styles.reminderRowHeader}>
                    <View style={styles.reminderStyleGroup}>
                        {REMINDER_STYLES.map((style) => {
                            const active = row.style === style.value;

                            return (
                                <TouchableOpacity
                                    key={style.value}
                                    onPress={() => updateReminder(index, { style: style.value })}
                                    style={[styles.reminderStylePill, {
                                        borderColor: active ? m.colors.primary : m.colors.border,
                                        backgroundColor: active ? `${m.colors.primary}1f` : 'transparent'
                                    }]}
                                >
                                    <Icon name={style.icon} size={14} color={active ? m.colors.primary : m.colors.subText} />
                                    <Text style={{ color: active ? m.colors.primary : m.colors.subText, fontSize: 12, fontWeight: '700' }}>
                                        {t(style.key, { defaultValue: style.defaultValue })}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                    {reminders.length > 1 && (
                        <TouchableOpacity
                            onPress={() => removeReminder(index)}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                            <Icon name="close-circle-outline" size={20} color={m.colors.subText} />
                        </TouchableOpacity>
                    )}
                </View>
                <View style={styles.reminderChipRow}>
                    {REMINDER_PRESETS.map((preset) => {
                        const active = !isCustom && row.minutesBefore === preset.value;

                        return (
                            <TouchableOpacity
                                key={preset.value}
                                onPress={() => updateReminder(index, { minutesBefore: preset.value, custom: false })}
                                style={[styles.reminderChip, {
                                    borderColor: active ? m.colors.primary : m.colors.border,
                                    backgroundColor: active ? `${m.colors.primary}1f` : 'transparent'
                                }]}
                            >
                                <Text style={{ color: active ? m.colors.primary : m.colors.subText, fontSize: 12, fontWeight: '600' }}>
                                    {t(preset.key, { defaultValue: preset.defaultValue })}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                    <TouchableOpacity
                        onPress={() => updateReminder(index, { custom: true })}
                        style={[styles.reminderChip, {
                            borderColor: isCustom ? m.colors.primary : m.colors.border,
                            backgroundColor: isCustom ? `${m.colors.primary}1f` : 'transparent'
                        }]}
                    >
                        <Text style={{ color: isCustom ? m.colors.primary : m.colors.subText, fontSize: 12, fontWeight: '600' }}>
                            {t('calendar_reminder_custom', { defaultValue: 'Egyéni' })}
                        </Text>
                    </TouchableOpacity>
                </View>
                {isCustom && (
                    <EditableField
                        label={t('calendar_reminder_custom_minutes', { defaultValue: 'Perccel előtte' })}
                        value={String(row.minutesBefore)}
                        onChangeText={(value) => {
                            const minutes = parseInt(String(value).replace(/[^0-9]/g, ''), 10);

                            updateReminder(index, { minutesBefore: Number.isFinite(minutes) ? minutes : 0, custom: true });
                        }}
                        keyboardType="numeric"
                        icon="timer-outline"
                    />
                )}
            </View>
        );
    };

    const handleSave = () => {
        // Sanitized rows (ints, deduped by minutesBefore, capped) travel beside
        // the legacy single reminderTime = smallest lead time, so older backend
        // docs / clients keep working unchanged.
        const cleanReminders = sanitizeReminders(reminders);

        onSave({
            title,
            description,
            startDate,
            startTime,
            endDate,
            endTime,
            expertId,
            clientName,
            clientPhone,
            clientEmail,
            reminders: cleanReminders,
            reminderMinutes: cleanReminders.length ? cleanReminders[0].minutesBefore : 30,
            notificationsEnabled,
            notifyByEmail,
            notifyBySms,
            notifyExpert,
            syncWithGoogle,
            syncWithApple,
            meetingUrl: meetingUrl.trim(),
            meetingProvider
        });
    };

    return (
        <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
            <ModalKeyboardAvoider style={styles.overlay} justify="flex-end">
                <View style={[styles.sheet, { backgroundColor: m.colors.surface }]}>
                    <View style={styles.header}>
                        <Text style={[styles.headerTitle, { color: m.colors.text }]}>
                            {busyMode
                                ? t('calendar_mark_busy_title')
                                : (mode === 'edit' ? t('calendar_edit_event') : t('calendar_create_event'))}
                        </Text>
                        <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                            <Icon name="close" size={24} color={m.colors.subText} />
                        </TouchableOpacity>
                    </View>

                    <ModalScrollBody style={styles.body} contentContainerStyle={styles.bodyContent}>
                            <EditableField
                                label={busyMode ? t('calendar_busy_reason') : t('event_title')}
                                value={title}
                                onChangeText={setTitle}
                                icon="format-title"
                            />
                            {!busyMode && (
                                <EditableField
                                    label={t('event_description')}
                                    value={description}
                                    onChangeText={setDescription}
                                    multiline
                                    icon="text"
                                />
                            )}
                            {!busyMode && (
                                <>
                                    <EditableField
                                        label={t('meeting_link_label')}
                                        value={meetingUrl}
                                        onChangeText={(value) => {
                                            setMeetingUrl(value);
                                            if (!value) {
                                                setMeetingProvider('');
                                            }
                                        }}
                                        placeholder="https://meet.google.com/abc-defg-hij"
                                        autoCapitalize="none"
                                        keyboardType="url"
                                        icon="video-outline"
                                    />
                                    {/* Generate a real link — one button per platform this
                                        deployment can actually mint on (Google Meet, Teams,
                                        Zoom, Webex, Whereby, Jitsi). OAuth platforms open
                                        their consent page first when not linked yet. */}
                                    <View style={styles.meetingActions}>
                                        {meetingProviders.map((entry) => (
                                            <PrimaryButton
                                                key={entry.value}
                                                label={t('meeting_link_generate_with', {
                                                    defaultValue: '{{provider}} link',
                                                    provider: entry.label
                                                })}
                                                icon={MEETING_PROVIDER_ICON[entry.value] || 'video'}
                                                variant="outline"
                                                loading={generatingMeeting === entry.value}
                                                onPress={() => handleGenerateMeeting(entry.value)}
                                            />
                                        ))}
                                    </View>
                                </>
                            )}

                            <TouchableOpacity onPress={() => setPicker('start')}>
                                <EditableField
                                    label={t('event_start')}
                                    value={`${startDate} ${startTime}`}
                                    editable={false}
                                    icon="calendar-start"
                                />
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => setPicker('end')}>
                                <EditableField
                                    label={t('event_end')}
                                    value={`${endDate} ${endTime}`}
                                    editable={false}
                                    icon="calendar-end"
                                />
                            </TouchableOpacity>

                            {showExpert && (
                                <ChipSelectField
                                    label={t('expert')}
                                    value={expertId}
                                    options={expertOptions}
                                    onSelect={setExpertId}
                                />
                            )}

                            {showExpert && (
                                <>
                                    <EditableField label={t('client_name')} value={clientName} onChangeText={setClientName} icon="account" />
                                    <EditableField label={t('client_phone')} value={clientPhone} onChangeText={setClientPhone} keyboardType="phone-pad" icon="phone" />
                                    <EditableField label={t('client_email')} value={clientEmail} onChangeText={setClientEmail} keyboardType="email-address" icon="email" />
                                </>
                            )}

                            {!busyMode && (
                                <>
                                    <SwitchField
                                        label={t('enable_notifications')}
                                        value={notificationsEnabled}
                                        onValueChange={setNotificationsEnabled}
                                    />
                                    {notificationsEnabled && (
                                        <>
                                            {/* Multi-reminder editor — up to MAX_REMINDERS rows,
                                                each with its own lead time + alert style. Quiet
                                                'notification' rows are pushed by the backend cron;
                                                'alarm' rows are scheduled locally on the phone
                                                (loud, offline-capable — see calendarReminders.js). */}
                                            <View style={styles.remindersWrap}>
                                                <Text style={[styles.remindersLabel, { color: m.colors.subText }]}>
                                                    {t('event_reminder')}
                                                </Text>
                                                {reminders.map(renderReminderRow)}
                                                {reminders.length < MAX_REMINDERS && (
                                                    <TouchableOpacity
                                                        onPress={addReminder}
                                                        style={[styles.addReminderBtn, { borderColor: m.colors.border }]}
                                                    >
                                                        <Icon name="plus" size={16} color={m.colors.primary} />
                                                        <Text style={{ color: m.colors.primary, fontWeight: '700', fontSize: 13 }}>
                                                            {t('calendar_reminder_add', { defaultValue: 'Emlékeztető hozzáadása' })}
                                                        </Text>
                                                    </TouchableOpacity>
                                                )}
                                                <Text style={[styles.reminderHint, { color: m.colors.subText }]}>
                                                    {t('calendar_reminder_alarm_hint', { defaultValue: 'Az ébresztő stílus hangos, riasztásszerű jelzést ad — offline is működik.' })}
                                                </Text>
                                            </View>
                                            <SwitchField
                                                label={t('notify_by_email')}
                                                value={notifyByEmail}
                                                onValueChange={setNotifyByEmail}
                                            />
                                            <SwitchField
                                                label={t('notify_by_sms')}
                                                value={notifyBySms}
                                                onValueChange={setNotifyBySms}
                                            />
                                            {showExpert && (
                                                <SwitchField
                                                    label={t('notify_expert')}
                                                    value={notifyExpert}
                                                    onValueChange={setNotifyExpert}
                                                />
                                            )}
                                        </>
                                    )}
                                    <SwitchField
                                        label={t('sync_with_google')}
                                        description={connection.google ? undefined : t('connection_not_connected')}
                                        value={syncWithGoogle && Boolean(connection.google)}
                                        onValueChange={(v) => connection.google && setSyncWithGoogle(v)}
                                    />
                                    <SwitchField
                                        label={t('sync_with_apple')}
                                        description={connection.apple ? undefined : t('connection_not_connected')}
                                        value={syncWithApple && Boolean(connection.apple)}
                                        onValueChange={(v) => connection.apple && setSyncWithApple(v)}
                                    />
                                </>
                            )}

                            <View style={styles.actions}>
                                <PrimaryButton
                                    label={t('save')}
                                    icon="content-save"
                                    onPress={handleSave}
                                    loading={saving}
                                />
                                {mode === 'edit' && onDelete && (
                                    <PrimaryButton
                                        label={t('delete')}
                                        icon="delete-outline"
                                        tone="danger"
                                        variant="outline"
                                        onPress={onDelete}
                                    />
                                )}
                            </View>
                    </ModalScrollBody>
                </View>
            </ModalKeyboardAvoider>

            <DateTimeModal
                visible={picker === 'start'}
                isTime
                isHistory={false}
                setDate={setStartDate}
                setTime={setStartTime}
                onClose={() => setPicker(null)}
            />
            <DateTimeModal
                visible={picker === 'end'}
                isTime
                isHistory={false}
                setDate={setEndDate}
                setTime={setEndTime}
                onClose={() => setPicker(null)}
            />
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        backgroundColor: 'rgba(15,23,42,0.45)'
    },
    sheet: {
        maxHeight: '92%',
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
        overflow: 'hidden'
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 18,
        paddingVertical: 16,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: 'rgba(148,163,184,0.3)'
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '800'
    },
    body: {
        flexShrink: 1,
        paddingHorizontal: 16
    },
    bodyContent: {
        paddingTop: 12,
        paddingBottom: 24
    },
    actions: {
        marginTop: 8,
        gap: 10
    },
    meetingActions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: -4,
        marginBottom: 4
    },
    remindersWrap: {
        marginBottom: 10
    },
    remindersLabel: {
        fontSize: 12,
        fontWeight: '700',
        textTransform: 'uppercase',
        marginBottom: 8
    },
    reminderRow: {
        borderWidth: 1,
        borderRadius: 14,
        padding: 10,
        marginBottom: 8
    },
    reminderRowHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8
    },
    reminderStyleGroup: {
        flexDirection: 'row',
        gap: 8
    },
    reminderStylePill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 999,
        borderWidth: 1
    },
    reminderChipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6
    },
    reminderChip: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 999,
        borderWidth: 1
    },
    addReminderBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 9,
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'dashed'
    },
    reminderHint: {
        fontSize: 11,
        marginTop: 8,
        lineHeight: 15
    }
});

export default EventEditModal;
